// Generated by OrchestKit Claude Plugin
// Created: 2026-09-12

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Chargement, DateHeure, Erreur, formaterDateHeure } from '../../composants/communs.tsx';
import {
  BoutonImprimer,
  DataTable,
  Montant,
  Nombre,
  Sheet,
  formaterFcfa,
  exporterTableauPdf,
  type Colonne,
} from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { FormulaireRetour } from './FormulaireRetour.tsx';
import { REMBOURSEMENTS, type Retour } from './types.ts';

/**
 * Retours de marchandise — Q-039 à Q-042.
 *
 * Deux boutiques apparaissent parce qu'elles peuvent différer : le client
 * rend où il se présente, mais le poids financier reste à la boutique qui a
 * vendu (Q-041). Confondre les deux ferait porter la reprise à la mauvaise
 * caisse.
 */
export function Retours(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { boutiqueId, peut } = useSession();
  const [saisie, setSaisie] = useState(false);
  const consolide = boutiqueId === null;

  const { data, isPending, error } = useQuery({
    queryKey: ['retours', boutiqueId],
    queryFn: () =>
      appeler<{ donnees: Retour[] }>(
        `/retours${boutiqueId === null ? '' : `?boutique_id=${boutiqueId}`}`,
      ),
    select: (reponse) => reponse.donnees,
  });

  return (
    <>
      <Entete
        titre="Retours"
        sous="Marchandise rendue par les clients : reprise du stock et remboursement (Q-039 à Q-042)."
      />
      <Erreur erreur={error} />

      <div className="pile">
        {peut('retour.creer') &&
          (saisie ? (
            <FormulaireRetour
              onFerme={() => {
                setSaisie(false);
              }}
            />
          ) : (
            <div>
              <button
                type="button"
                onClick={() => {
                  setSaisie(true);
                }}
              >
                Nouveau retour
              </button>
            </div>
          ))}

        {isPending ? (
          <Chargement />
        ) : (
          <Sheet
            action={
              (data ?? []).length > 0 ? (
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Retours',
                        colonnes: colonnesRetours(consolide),
                        lignes: data ?? [],
                        nomFichier: 'retours',
                      }),
                    );
                  }}
                />
              ) : undefined
            }
          >
            <DataTable
              lignes={data ?? []}
              cleDe={(retour: Retour) => retour.id}
              vide="Aucun retour enregistré."
              colonnes={colonnesRetours(consolide)}
            />
          </Sheet>
        )}
      </div>
    </>
  );
}

function colonnesRetours(consolide: boolean): Colonne<Retour>[] {
  return [
    {
      cle: 'numero',
      entete: 'N°',
      rendu: (retour: Retour) => <span className="mono">{retour.numero}</span>,
      texte: (retour) => retour.numero,
    },
    {
      cle: 'date',
      entete: 'Date',
      rendu: (retour: Retour) => <DateHeure valeur={retour.horodatage_serveur} />,
      texte: (retour) => formaterDateHeure(retour.horodatage_serveur),
    },
    {
      cle: 'vente',
      entete: 'Vente',
      rendu: (retour: Retour) => <span className="mono">{retour.vente_numero}</span>,
      texte: (retour) => retour.vente_numero,
    },
    // En vue consolidée seulement : sur une boutique unique, ces colonnes
    // répéteraient la même valeur à chaque ligne.
    ...(consolide
      ? [
          {
            cle: 'boutique',
            entete: 'Rendu à',
            rendu: (retour: Retour) => retour.boutique,
            texte: (retour: Retour) => retour.boutique,
          },
          {
            cle: 'origine',
            entete: 'Vendu par',
            rendu: (retour: Retour) => retour.boutique_origine,
            texte: (retour: Retour) => retour.boutique_origine,
          },
        ]
      : []),
    {
      cle: 'client',
      entete: 'Client',
      rendu: (retour: Retour) => retour.client ?? '—',
      texte: (retour) => retour.client ?? '—',
    },
    {
      cle: 'articles',
      entete: 'Articles',
      numerique: true,
      rendu: (retour: Retour) => (
        <>
          <Nombre valeur={retour.articles} />
          {retour.articles_rebut > 0 && <span> dont {retour.articles_rebut} au rebut</span>}
        </>
      ),
      texte: (retour) =>
        retour.articles_rebut > 0
          ? `${String(retour.articles)} (dont ${String(retour.articles_rebut)} au rebut)`
          : String(retour.articles),
    },
    {
      cle: 'montant',
      entete: 'Montant',
      numerique: true,
      rendu: (retour: Retour) => <Montant valeur={retour.montant} />,
      texte: (retour) => formaterFcfa(retour.montant),
    },
    {
      cle: 'remboursement',
      entete: 'Remboursement',
      rendu: (retour: Retour) => REMBOURSEMENTS[retour.remboursement],
      texte: (retour) => REMBOURSEMENTS[retour.remboursement],
    },
    {
      cle: 'fenetre',
      entete: 'Fenêtre',
      // Une dérogation doit se VOIR dans l'historique : c'est le seul
      // endroit où un gérant peut remarquer qu'elle devient une habitude.
      rendu: (retour: Retour) => (retour.hors_fenetre ? 'Dérogation' : '—'),
      texte: (retour) => (retour.hors_fenetre ? 'Dérogation' : '—'),
    },
    {
      cle: 'par',
      entete: 'Enregistré par',
      rendu: (retour: Retour) => retour.enregistre_par,
      texte: (retour) => retour.enregistre_par,
    },
  ];
}
